import { InputType } from '../formbuilder/input-type';
import { IFormValidator } from '../formbuilder/form-validator.interface';
import { FormValidatorType } from '../formbuilder/form-validator-type';
import { IFormControl, FormControlOption } from '../formbuilder/formcontrol.interface';
import { IResponse } from '../../model/api/response.entity.interface';
import { IFormFieldEntity } from '../../model/api/form/form-field.entity.interface';
import { IFieldValidatorEntity } from '../../model/api/form/field-validator.entity.interface';
import { Category, FieldModel } from '../../model/myria/app/DynamicForm';
import { FORM_VALIDATION_NAMES, LITE_REGISTRATION_FIELDS } from '../myria/app-variables';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import {
  IBankDepositRequirementsCategoryEntity,
  IBankDepositRequirementsFieldEntity,
  IBankDepositTransactionRequirementsResponseEntity
} from '../../model/api/bank-deposit';
import { TranslateService } from '@ngx-translate/core';

/**
 * @description Maps form collection endpoint responses from the remittance API.
 */
export class FormControlMapper {
  private static translateService: TranslateService = null;
  
  static init(translateService: TranslateService) {
    FormControlMapper.translateService = translateService;
  }

  static fromFormResponse(response: IResponse, _?: string): IFormControl[] {
    const formNames = Object.keys(response.formData);
    return formNames.reduce((accum, formName) => [...accum, ...this.mapControls(response.formData[formName].formFields, formName, response.model)], [])
  }

  /** transactionRequirements form definition, mapped onto the legacy IFormControl[] shape. */
  static fromTransactionRequirements(response: IBankDepositTransactionRequirementsResponseEntity): IFormControl[] {
    if (!response || !response.FormData || !response.FormData.Categories)
      return [];

    const categories = response.FormData.Categories
      .filter((category) => !!category.Fields)
      .sort((a, b) => a.Order - b.Order);

    // The screen renders the bank itself, so those fields never become controls.
    const rendered = categories.map((category) => ({
      category,
      fields: category.Fields
        .filter((field) => this.isRenderedRequirementsField(field))
        .sort((a, b) => a.FieldOrder - b.FieldOrder)
    }));

    // DependsOn is a numeric FieldId while controls are keyed by name, so index the rendered
    // fields only — a dependency on a field the form does not carry has no control to bind to.
    const idsByFieldId: { [fieldId: number]: string } = {};
    rendered.forEach((entry) => entry.fields.forEach((field) => {
      idsByFieldId[field.FieldId] = this.fixCasingOfId(field.FieldName);
    }));

    return rendered.reduce((controls, entry) => {
      const mapped = entry.fields.map((field) => this.mapTransactionRequirementsField(field, entry.category, idsByFieldId));
      return [...controls, ...mapped];
    }, []);
  }

  static isRenderedRequirementsField(field: IBankDepositRequirementsFieldEntity): boolean {
    // A nameless field has no control to key, and fixCasingOfId would throw on it.
    if (!field.FieldName)
      return false;
    const id = this.fixCasingOfId(field.FieldName);
    return id !== 'bankId' && id !== 'bankName';
  }

  static mapTransactionRequirementsField(
    field: IBankDepositRequirementsFieldEntity,
    category: IBankDepositRequirementsCategoryEntity,
    idsByFieldId: { [fieldId: number]: string }
  ): IFormControl {
    const id = this.fixCasingOfId(field.FieldName);
    const control: IFormControl = {
      id,
      parent: category.Title,
      label: field.Label,
      nameOverride: null,
      type: this.mapTransactionRequirementsFieldType(field.FieldType, id),
      options: (field.Options) ? field.Options.filter(o => o.Value !== '').map(o => ({ value: o.Value, text: o.Name })) : null,
      validators: this.mapTransactionRequirementsValidators(field),
      disabled: field.Disabled,
      placeholder: field.Placeholder || field.Label || id,
      defaultValue: field.DefaultValue,
      value: field.DefaultValue,
      category: category.CategoryId,
      hint: null,
      isProfileRefresh: field.IsProfileRefresh
    };

    // One FieldModel for both the parent link and picker options — mapControls overwrites the
    // parent when it converts a long option list, breaking dependent fields.
    // dependsOn is deliberately left unset: it is read as a control name against the CST numeric
    // field ids, which these name-keyed controls do not use. The parent link goes through FieldModel.
    const fieldModelOptions: any = {};
    const parentId = field.DependsOn ? idsByFieldId[field.DependsOn] : null;
    if (parentId) {
      fieldModelOptions.FieldId = id;
      fieldModelOptions.Parent = parentId;
      fieldModelOptions.ShowOnParentValues = field.ShowOnParentValues;
    }
    else if (field.DependsOn)
      LoggerProvider.info(FormControlMapper, `Bank deposit requirements field ${id} depends on field ${field.DependsOn}, which the form does not render, showing it unconditionally`);

    if (control.type === InputType.OptionList && control.options) {
      control.options.forEach((option) => FormControlMapper.getTranslatedOption(id, option));

      // Same threshold the legacy mapping uses for the MyRia picker.
      if (control.options.length > 5) {
        control.type = InputType.searchableSelect;
        fieldModelOptions.Options = control.options.map((option) => ({ Id: option.value, Name: option.text }));
      }
    }

    if (Object.keys(fieldModelOptions).length > 0)
      control.fieldModel = new FieldModel(fieldModelOptions);

    return control;
  }

  /** Lowercase FieldType to InputType. Unrecognised falls back to Text — undefined breaks rendering. */
  static mapTransactionRequirementsFieldType(fieldType: string, id: string): InputType {
    switch ((fieldType || '').toLowerCase()) {
      case 'text':
        return InputType.Text;
      case 'number':
        return InputType.Number;
      case 'email':
        return InputType.Email;
      case 'select':
      case 'optionlist':
        return InputType.OptionList;
      case 'searchableselect':
        return InputType.searchableSelect;
      case 'date':
        return InputType.Date;
      case 'phone':
      case 'phonenumber':
        return InputType.phoneNumber;
      case 'hidden':
        return InputType.Hidden;
      case 'addresslookup':
        return InputType.Loqate;
      case 'city':
        // No City member and no city renderer on this form; the field and its validation survive.
        LoggerProvider.info(FormControlMapper, `Bank deposit requirements field ${id} is type city, rendering as text`);
        return InputType.Text;
      default:
        LoggerProvider.warning(FormControlMapper, LoggerEvent.ProviderException, `Unmapped bank deposit requirements field type '${fieldType}' for ${id}, rendering as text`);
        return InputType.Text;
    }
  }

  /** Sibling of mapValidators: { Type, Value, Message } is not the legacy IFieldValidatorEntity shape. */
  static mapTransactionRequirementsValidators(field: IBankDepositRequirementsFieldEntity): IFormValidator[] {
    const validators: IFormValidator[] = [];
    let hasRequired = false;

    (field.Validations || []).forEach((validation) => {
      const type = (validation.Type || '').toLowerCase();
      const message = validation.Message;
      if (type === FORM_VALIDATION_NAMES.REQUIRED) {
        hasRequired = true;
        validators.push({ type: FormValidatorType.Required, message });
      }
      else if (type === FORM_VALIDATION_NAMES.MIN_LENGTH)
        validators.push({ type: FormValidatorType.MinLength, minLength: Number(validation.Value), message });
      else if (type === FORM_VALIDATION_NAMES.MAX_LENGTH)
        validators.push({ type: FormValidatorType.MaxLength, maxLength: Number(validation.Value), message });
      else if (type === FORM_VALIDATION_NAMES.REGEX) {
        try {
          validators.push({ type: FormValidatorType.Regex, regex: new RegExp(validation.Value), message });
        } catch (ex) {
          LoggerProvider.exception(FormControlMapper, ex, LoggerEvent.ProviderException, `Invalid regex on bank deposit requirements field ${field.FieldName}, validator skipped`);
        }
      }
      // Unrecognised types ignored, as the legacy CST mapping does.
    });

    if (field.Required && !hasRequired)
      validators.unshift({ type: FormValidatorType.Required });

    return validators;
  }

  static mapControls(
    controlEntities: IFormFieldEntity[],
    parentFormName: string,
    model: any
  ): IFormControl[] {
    return controlEntities.map((controlEntity) => {
      const control: IFormControl = {
        id: this.fixCasingOfId(controlEntity.id),
        parent: parentFormName,
        label: controlEntity.label || controlEntity.additionalText,
        nameOverride: null,
        additionalText: controlEntity.additionalText,
        type: <InputType>controlEntity.fieldType,
        options: (controlEntity.options) ? <FormControlOption[]>controlEntity.options.filter(o => o.value !== '') : null,
        validators: this.mapValidators(controlEntity.validators),
        disabled: controlEntity.disabled,
        placeholder: this.getPlaceholder(controlEntity),
        defaultValue: controlEntity.value,
        value: (controlEntity.prefillValue) ? controlEntity.prefillValue : controlEntity.value,
        isValid: controlEntity.isValid,
        category: controlEntity.fieldCategory,
        hint: null,
        pattern: controlEntity.pattern,
        storeValueWithPattern: controlEntity.storeValueWithPattern,
        screenType: controlEntity.screenType,
        screenCategory: controlEntity.screenCategory,
        screenId: controlEntity.screenId,
        screenIdOrder: controlEntity.screenIdOrder,
        isProfileRefresh: controlEntity.isProfileRefresh,
        sources: controlEntity.sources
      };
      
      if(controlEntity.parent){
        control.fieldModel = new FieldModel(
          {
              FieldId: this.fixCasingOfId(controlEntity.id),
              Parent: this.fixCasingOfId(controlEntity.parent),
              ShowOnParentValues: controlEntity['showOnParentValues'],
            }
        );
      }

      // adjust options for phone number country code
      if (control.id === 'phoneNumberCountryCode' && control.type === InputType.OptionList) {
        control.options.forEach((option) => {
          option.text = `+${option.value} ${option.text}`;
          option.selectedText = `+${option.value}`;
          option.selectedValue = option.value;
        });
      }

      if (control.type === InputType.OptionList) {
        control.options.forEach((option) => FormControlMapper.getTranslatedOption(controlEntity.id, option));

        // if an option select has more than 5 elements Roberto wants to use the MyRia picker
        if (control.options.length > 5) {
          const options = [];
          control.options.forEach((option) => {
            options.push({
              Id: option.value,
              Name: option.text
            });
          });
          control.type = InputType.searchableSelect;
          control.fieldModel = new FieldModel({
            Options: options
          });
        }
        else {
          const option = control.options.find((option) => (option.selectedValue) ? (option.selectedValue === control.value) : (option.value === control.value));
          if (option) {
            control.value = (option.selectedValue) ? option.selectedValue : option.value;
          }
        }
      }

      // Mark the field as required here in the placeholder. I could see this changing in the future.
      /*if (control.validators.some(v => v.type === FormValidatorType.Required)) {
        control.placeholder += '*';
      }*/

      // Set value
      for (const m in model) {
        if (m.toLowerCase() === control.id.toLowerCase()) {
          control.value = model[m];
        }
      }

      return control;
    })
  }
  static getTranslatedOption(controlEntityId, option) {
    if (FormControlMapper.translateService && option) {
      if (option.value && option.text) {
        if (controlEntityId)
          controlEntityId = FormControlMapper.fixCasingOfId(controlEntityId);

        // Normal/remittance format
        const key = `form.translatedOptions.${controlEntityId}_${option.value}`;
        const translated = FormControlMapper.translateService.instant(key);
        if (translated && translated.indexOf(key) === -1)
          option.text = translated;
      } else if (option.Id && option.Name) {
        // CST format
        const key = `form.translatedOptions.${controlEntityId}_${option.Id}`;
        const translated = FormControlMapper.translateService.instant(key);
        if (translated && translated.indexOf(key) === -1)
          option.Name = translated;
      }
    }
    return option;
  }

  static fixCasingOfId(id: string): string {
    const splitId = id.split('');
    splitId[0] = splitId[0].toLowerCase();
    return splitId.join('');
  }

  static getPlaceholder(controlEntity: IFormFieldEntity): string {
    switch (controlEntity.fieldType) {
      case InputType.OptionList:
        // Typically the placeholder is the option with an empty value
        const placeHolderOption = controlEntity.options.filter(
          o => !o.value || o.value.length === 0
        );
        return placeHolderOption.length > 0
          ? placeHolderOption[0].text
          : '';
      default:
        // Really the only case where the ID should/would be used here is if there is bad content data with no label
        return controlEntity.placeholder || controlEntity.label || controlEntity.id;
    }
  }

  static mapValidators(
    controlValidators: IFieldValidatorEntity[]
  ): IFormValidator[] {
    const formValidators: IFormValidator[] = [];
    controlValidators.forEach(validator => {
      // Sometimes the API uses returns the "id" property instead of the "name" property.
      // TODO: improve API 
      if (validator.id && !validator.name) {
        validator.name = validator.id;
      }

      const _type = validator.name.toLowerCase().includes(`regex`)
        ? FormValidatorType.Regex
        : validator.name;
      formValidators.push({
        type: <FormValidatorType>_type,
        regex: new RegExp(validator.regex),
        minLength: validator.minLength,
        maxLength: validator.maxLength,
        minValue: validator.minValue,
        maxValue: validator.maxValue,
        message: validator.message,
        regexValueOptions: validator.regexValueOptions
      });
    });
    return formValidators;
  }

  public static initFormFields(): IFormControl{
    return {id: '', parent: '', value: '', nameOverride: '', additionalText: '', label: '', type: InputType.Text, options: [], validators: [], placeholder: '', disabled: false, selectedValue: '', hasAsyncValues: false};
  }
  
  public static initFormValidator(): IFormValidator{
    return {type: null, regex: null, minLength: null, maxLength: null, minValue: null, maxValue: null, message: null};
  }
  
  public static initFormOptions(): FormControlOption{
    return {category: null, value: '', text: ''};
  }

  public static mapRequiredFields(requiredCst: any){

    let customerFields: IFormControl[] = [];
    let recipientFields: IFormControl[] = [];

    requiredCst.forEach(rf => {
      rf.Fields.forEach(async f => {
        const formControlFields = this.mapCstField(f);
        formControlFields.parent = rf.Title;

        (rf.Category == 2 || rf.Category == 3) ? customerFields.push(Object.assign({}, formControlFields)) : recipientFields.push(Object.assign({}, formControlFields));
      });
    });
    return [customerFields,recipientFields];
  }

  public static mapCstField(model) : IFormControl {
    const field = this.initFormFields();
    if (model.Id)
      field.id = model.Id.toString();
    else
      field.id = model.FieldId.toString();
    field.label = model.Label;
    field.placeholder = model.Placeholder;
    field.type = this.mapFieldType(model.Type, field.id);
    field.nameOverride = null;
    field.validators=[];
    field.hasAsyncValues = model.HasAsyncValues;
    field.dependsOn = model.DependsOn;
    field.value = (model.PrefillValue) ? (typeof model.PrefillValue === 'string' ? model.PrefillValue.trim() : model.PrefillValue) : model.Value;
    field.disabled = model.Readonly;
    field.fieldModel = model;
    field.category = model.FieldCategory;
    field.screenType = model.ScreenType;
    field.screenCategory = model.ScreenCategory;
    field.screenId = model.ScreenId;
    field.screenIdOrder = model.ScreenIdOrder;
    field.isProfileRefresh = model.IsProfileRefresh;
    field.sources = model.Sources;
    if (model.FieldValidations)
      model.FieldValidations.forEach(v => {
        let validator: IFormValidator = this.initFormValidator();

        let  type = null;
        if (v.Type)
          type = v.Type.toLowerCase();
        if(type === FORM_VALIDATION_NAMES.REQUIRED) {
          validator.type = FormValidatorType.Required;
        }
        else if(type === FORM_VALIDATION_NAMES.MIN_LENGTH) {
            validator.type = FormValidatorType.MinLength; 
            validator.minLength = v.Value;
        }
        else if(type === FORM_VALIDATION_NAMES.MAX_LENGTH) {
          validator.type = FormValidatorType.MaxLength; 
          validator.maxLength = v.Value;
        }
        else if(type === FORM_VALIDATION_NAMES.REGEX) {
          validator.type = FormValidatorType.Regex; 
          validator.regex = v.Value;

          if (v.RegexValueOptions) 
            validator.regexValueOptions = v.RegexValueOptions;

        }
        else if(type === FORM_VALIDATION_NAMES.CUSTOM) {
          validator.type = FormValidatorType.Custom; 
          validator.test = v.Value;
        }
        
        else        
          return;
          
        field.validators.push(Object.assign({},validator));
      });

    if (model.Options) {
      field.options = this.mapFieldOptions(model.Options);
      field.options.forEach(option => FormControlMapper.getTranslatedOption(field.id, option));
    }
    return field;
  }

  public static mapCategoryToFields(category: Category): IFormControl[] {
    var fields = [];
    if (!category.Fields)
      return fields;
    
    for (let i=0; i < category.Fields.length; i++) {
      const model: FieldModel = category.Fields[i];
      const field: IFormControl = this.mapCstField(model);
      fields.push(field);
    }
    fields = fields.sort((a,b) => (a.fieldModel.fieldOrder == b.fieldModel.fieldOrder) ? 0 : a.fieldModel.fieldOrder > b.fieldModel.fieldOrder ? 1 : -1);
    return fields;
  }

  public static mapFieldType(type: string, id: string) : InputType {
    if (type === 'number')
      return InputType.Number;
    else if (type === 'text')
      return InputType.Text;
    else if (type === 'email')
      return InputType.Email;      
    else if (type === 'select')
      return InputType.OptionList;
    else if (type === 'date')
      return InputType.Date;
    else if (type === 'searchableSelect') {
      if (id === LITE_REGISTRATION_FIELDS.COUNTRY.Id)
        return InputType.Country;
      else
        return InputType.searchableSelect;
    }
    else if (type === 'phoneNumber')
      return InputType.phoneNumber;
    else if (type === 'boolean')
      return InputType.Boolean;
    return InputType[type];
  }

  public static mapFieldOptions(options) {  
    const result = [];
    options.forEach(o => {
      let opt : FormControlOption = this.initFormOptions();
      opt.value = o.Id;
      opt.text = o.Name;
      opt.displayPriority = o.DisplayPriority;
      result.push(Object.assign({},opt));
    });
    return result;
  }
}


// WEBPACK FOOTER //
// ./src/providers/formbuilder/formcontrol-mapper.ts